import { useCallback } from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText, Box } from '@mui/material';
import { Visibility as ViewerIcon } from '@mui/icons-material';
import type { Study } from '../../api/types';
import { getAllViewers, getDefaultViewer } from '../viewer/viewerRegistry';
import type { ViewerProvider } from '../viewer/types';
import { gwtStyles } from '../../config/theme';

interface ViewerPickerMenuProps {
  /** Anchor position (for right-click / context menu style) */
  anchorPosition?: { top: number; left: number };
  /** Anchor element (for toolbar button style) */
  anchorEl?: HTMLElement | null;
  study: Study;
  onSelect: (viewerId: string) => void;
  onClose: () => void;
}

/**
 * Popup menu that lets the user choose which viewer to open a study with.
 * Shows all registered ViewerProviders from the viewer registry.
 */
export const ViewerPickerMenu = ({
  anchorPosition,
  anchorEl,
  study,
  onSelect,
  onClose,
}: ViewerPickerMenuProps) => {
  const viewers = getAllViewers();
  const defaultViewer = getDefaultViewer();

  const handleSelect = useCallback(
    (viewer: ViewerProvider) => {
      onClose();
      onSelect(viewer.id);
    },
    [onSelect, onClose],
  );

  return (
    <Menu
      open
      onClose={onClose}
      anchorReference={anchorPosition ? 'anchorPosition' : 'anchorEl'}
      anchorPosition={anchorPosition}
      anchorEl={anchorEl}
      slotProps={{
        paper: {
          sx: {
            minWidth: 220,
            borderRadius: '8px',
            border: `1px solid ${gwtStyles.borderSubtle}`,
            backgroundColor: gwtStyles.mainView,
          },
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2, py: 0.75, borderBottom: `1px solid ${gwtStyles.borderSubtle}` }}>
        <Box sx={{ fontSize: 11, fontWeight: 600, color: gwtStyles.textPrimary }}>
          Open with...
        </Box>
        <Box sx={{ fontSize: 10, color: gwtStyles.textSecondary }}>
          {study.name} — {study.acc}
        </Box>
      </Box>

      {viewers.map((viewer) => (
        <MenuItem
          key={viewer.id}
          onClick={() => handleSelect(viewer)}
          sx={{
            fontSize: 12,
            py: 0.5,
            '&:hover': { backgroundColor: 'rgba(91,141,239,0.12)' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 28 }}>
            <ViewerIcon sx={{ fontSize: 16, color: viewer.id === defaultViewer.id ? gwtStyles.accentPrimary : gwtStyles.textSecondary }} />
          </ListItemIcon>
          <ListItemText
            primary={viewer.name}
            secondary={viewer.id === defaultViewer.id ? 'Default' : undefined}
            primaryTypographyProps={{ fontSize: 12 }}
            secondaryTypographyProps={{ fontSize: 10, color: gwtStyles.accentPrimary }}
          />
        </MenuItem>
      ))}
    </Menu>
  );
};
